import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  getLoggedAdmin,
  isMasterAdmin,
  hasModuleAccess,
  PERMISSION_MODULES
} from '../Function/Permission';

const Header = ({ children }) => {
  const [isToggled, setIsToggled] = useState(false);
  const location = useLocation();
  const currentPath = location.pathname.toLowerCase();

  const authToken = sessionStorage.getItem('authToken');
  const toggleSidebar = () => { setIsToggled(!isToggled); };
  const baseurl = process.env.REACT_APP_BASE_API_URL;

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Change Password Modal States
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPass, setShowCurrentPass] = useState(false);
  const [showNewPass, setShowNewPass] = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');

    if (!currentPassword) {
      toast.warn('Please enter your current password.');
      return;
    }
    if (newPassword.length < 6) {
      toast.warn('New password must be at least 6 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.warn('New password and confirmation password do not match.');
      return;
    }

    setIsChangingPassword(true);
    try {
      const response = await fetch(`${baseurl}/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
          confirm_password: confirmPassword,
        }),
      });

      const data = await response.json();

      if (response.ok && data.status) {
        toast.success(data.message || 'Password changed successfully!');
        setShowPasswordModal(false);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        const msg = data.message || 'Failed to change password.';
        setPasswordError(msg);
        toast.error(msg);
      }
    } catch (err) {
      console.error('Change password error:', err);
      toast.error('Network error. Please try again.');
    } finally {
      setIsChangingPassword(false);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [isOpenForms, setIsOpenForms] = useState(false);
  const toggleMenuForms = () => {
    setIsOpenForms(!isOpenForms);
  };

  const [isOpenBlockchain, setIsOpenBlockchain] = useState(
    currentPath.startsWith('/treasury') || currentPath.startsWith('/blockchain')
  );
  const toggleBlockchainMenu = (e) => {
    if (e) e.preventDefault();
    setIsOpenBlockchain(prev => !prev);
  };

  const logout = async () => {
    try {
      if (!authToken) {
        handleLogout();
        return;
      }

      const response = await fetch(`${baseurl}/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (response.status === 429) {
        console.error("Rate limit exceeded. Please try again later.");
        return;
      }

      await response.json();
      handleLogout();
    } catch (error) {
      console.error('Logout error:', error);
      handleLogout();
    }
  };

  const handleLogout = () => {
    sessionStorage.clear();
    localStorage.clear();
    window.location.href = "/login";
  };

  const isRouteActive = (route) => {
    if (route === '/') {
      return currentPath === '/' || currentPath === '/dashboard';
    }
    return currentPath.startsWith(route);
  };

  // Determine current route's required module key
  const getModuleKeyForPath = (path) => {
    if (path === '/' || path === '/dashboard') return 'dashboard';
    if (path.startsWith('/client')) return 'clients';
    if (path.startsWith('/trade')) return 'trades';
    if (path.startsWith('/rank')) return 'rank';
    if (path.startsWith('/reward')) return 'rewards';
    if (path.startsWith('/matching-bonus')) return 'matching';
    if (path.startsWith('/client-withdraw') || path.startsWith('/withdraw')) return 'withdrawals';
    if (path.startsWith('/master-wallet')) return 'master_wallet';
    if (path.startsWith('/treasury') || path.startsWith('/blockchain')) return 'treasury';
    if (path.startsWith('/support')) return 'support';
    if (path.startsWith('/admin')) return 'subadmins';
    if (path.startsWith('/notification')) return 'notifications';
    if (path.startsWith('/setting')) return 'settings';
    return null;
  };

  const loggedAdmin = getLoggedAdmin();
  const isMaster = isMasterAdmin(loggedAdmin);
  const currentModuleKey = getModuleKeyForPath(currentPath);
  const isPageAllowed = currentModuleKey ? hasModuleAccess(currentModuleKey, loggedAdmin) : true;

  return (
    <div
      dir="ltr"
      data-nav-layout="vertical"
      loader="disable"
      data-vertical-style={isToggled ? "overlay" : "hidden"}
      data-toggled={isToggled ? "icon-overlay-close" : "icon-overlay-open"}
    >
      {/* app-header */}
      <header className="app-header">
        <div className="main-header-container container-fluid">
          {/* header-content-left */}
          <div className="header-content-left">
            <div className="header-element">
              <div className="horizontal-logo">
                <Link to="/" className="header-logo">
                  <img src="assets/images/logo/walletIcon.png" alt="logo" className="desktop-logo" />
                  <img src="assets/images/logo/walletIcon.png" alt="logo" className="toggle-logo" />
                  <img src="assets/images/logo/walletIcon.png" alt="logo" className="desktop-white" />
                  <img src="assets/images/logo/walletIcon.png" alt="logo" className="toggle-white" />
                </Link>
              </div>
            </div>

            <div className="header-element">
              <button
                type="button"
                aria-label="Hide Sidebar"
                className="sidemenu-toggle header-link animated-arrow hor-toggle horizontal-navtoggle btn btn-link p-0 border-0"
                onClick={toggleSidebar}
              >
                <i className="header-icon fe fe-align-left" />
              </button>
            </div>
          </div>

          {/* header-content-right */}
          <div className="header-content-right">
            {hasModuleAccess('support', loggedAdmin) && (
              <div className="header-element messages-dropdown">
                <Link to="/support" className="header-link" aria-label="Support">
                  <svg xmlns="http://www.w3.org/2000/svg" className="header-link-icon" height="24px" viewBox="0 0 24 24" width="24px" fill="currentColor">
                    <path d="M0 0h24v24H0V0z" fill="none" />
                    <path d="M22 6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6zm-2 0l-8 5-8-5h16zm0 12H4V8l8 5 8-5v10z" />
                  </svg>
                  <span className="pulse-danger" />
                </Link>
              </div>
            )}

            {hasModuleAccess('notifications', loggedAdmin) && (
              <div className="header-element notifications-dropdown main-header-notification">
                <Link to="/notification" className="header-link" aria-label="Notifications">
                  <svg xmlns="http://www.w3.org/2000/svg" className="header-link-icon" height="24px" viewBox="0 0 24 24" width="24px" fill="currentColor">
                    <path d="M0 0h24v24H0V0z" fill="none" />
                    <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2zm-2 1H8v-6c0-2.48 1.51-4.5 4-4.5s4 2.02 4 4.5v6z" />
                  </svg>
                  <span className="pulse-success" />
                </Link>
              </div>
            )}

            {/* Profile & Role Badge */}
            <div className="header-element headerProfile-dropdown d-flex align-items-center gap-2">
              <button
                type="button"
                className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1 rounded-pill px-2 py-1 shadow-none"
                onClick={() => {
                  setPasswordError('');
                  setCurrentPassword('');
                  setNewPassword('');
                  setConfirmPassword('');
                  setShowPasswordModal(true);
                }}
                title="Change Account Password"
              >
                <i className="fa fa-key fs-11"></i>
                <span className="d-none d-lg-inline fs-11 fw-semibold">Change Password</span>
              </button>
              <div className="text-end d-none d-md-block">
                <div className="fw-bold fs-12 text-dark lh-1">{loggedAdmin?.name || 'Administrator'}</div>
                <span className={`badge ${isMaster ? 'bg-primary-transparent text-primary' : 'bg-secondary-transparent text-secondary'} fs-10 px-1 py-0 mt-1`}>
                  {isMaster ? 'Master Admin' : 'Sub-Admin'}
                </span>
              </div>
              <Link to={hasModuleAccess('subadmins', loggedAdmin) ? '/admin' : '#'} className="header-link" aria-label="Admin Profile">
                <img src="assets/images/profile/profile1.avif" alt="Profile" width={37} height={37} className="rounded-circle border" />
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* app-sidebar */}
      <aside className="app-sidebar sticky" id="sidebar" data-vertical-style={isToggled ? "overlay" : "hidden"} data-toggled={isToggled ? "open" : "close"}>
        <div className="main-sidebar-header">
          <Link to="/" className="header-logo">
            <img src="assets/images/logo/walletIcon.png" alt="logo" className="desktop-logo" />
            <img src="assets/images/logo/walletIcon.png" alt="logo" className="toggle-logo" />
            <img src="assets/images/logo/walletIcon.png" alt="logo" className="desktop-white" />
            <img src="assets/images/logo/walletIcon.png" alt="logo" className="toggle-white" />
          </Link>
        </div>

        <div className="main-sidebar" id="sidebar-scroll">
          <nav className="main-menu-container nav nav-pills flex-column sub-open">
            <div className="slide-left" id="slide-left">
              <svg xmlns="http://www.w3.org/2000/svg" fill="#7b8191" width={24} height={24} viewBox="0 0 24 24">
                <path d="M13.293 6.293 7.586 12l5.707 5.707 1.414-1.414L10.414 12l4.293-4.293z" />
              </svg>
            </div>

            <ul className="main-menu">
              {isMobile && (
                <li className="slide__category">
                  <span className="category-name">Main</span>
                  <i className="fe fe-align-justify side-menu__angle" onClick={toggleSidebar}></i>
                </li>
              )}

              {/* Dashboard */}
              {hasModuleAccess('dashboard', loggedAdmin) && (
                <li className="slide">
                  <Link to="/" className={`side-menu__item ${isRouteActive('/') ? 'active' : ''}`}>
                    <svg xmlns="http://www.w3.org/2000/svg" className="side-menu__icon" viewBox="0 0 24 24">
                      <path d="M0 0h24v24H0V0z" fill="none" />
                      <path d="M5 5h4v6H5zm10 8h4v6h-4zM5 17h4v2H5zM15 5h4v2h-4z" opacity=".3" />
                      <path d="M3 13h8V3H3v10zm2-8h4v6H5V5zm8 16h8V11h-8v10zm2-8h4v6h-4v-6zM13 3v6h8V3h-8zm6 4h-4V5h4v2zM3 21h8v-6H3v6zm2-4h4v2H5v-2z" />
                    </svg>
                    <span className="side-menu__label">Dashboard</span>
                  </Link>
                </li>
              )}

              {/* Users & Trades Category */}
              {(hasModuleAccess('clients', loggedAdmin) || hasModuleAccess('trades', loggedAdmin) || hasModuleAccess('rank', loggedAdmin)) && (
                <>
                  <li className="slide__category">
                    <span className="category-name">Users & Trades</span>
                  </li>

                  {hasModuleAccess('clients', loggedAdmin) && (
                    <li className="slide">
                      <Link to="/clients" className={`side-menu__item ${isRouteActive('/client') ? 'active' : ''}`}>
                        <svg xmlns="http://www.w3.org/2000/svg" className="side-menu__icon" viewBox="0 0 24 24">
                          <path d="M0 0h24v24H0V0z" fill="none" />
                          <path d="M12 4c-4.42 0-8 3.58-8 8s3.58 8 8 8 8-3.58 8-8-3.58-8-8-8zm3.5 4c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5-1.5-.67-1.5-1.5.67-1.5 1.5-1.5zm-7 0c.83 0 1.5.67 1.5 1.5S9.33 11 8.5 11 7 10.33 7 9.5 7.67 8 8.5 8zm3.5 9.5c-2.33 0-4.32-1.45-5.12-3.5h1.67c.7 1.19 1.97 2 3.45 2s2.76-.81 3.45-2h1.67c-.8 2.05-2.79 3.5-5.12 3.5z" opacity=".3" />
                          <circle cx="15.5" cy="9.5" r="1.5" />
                          <circle cx="8.5" cy="9.5" r="1.5" />
                          <path d="M12 16c-1.48 0-2.75-.81-3.45-2H6.88c.8 2.05 2.79 3.5 5.12 3.5s4.32-1.45 5.12-3.5h-1.67c-.69 1.19-1.97 2-3.45 2zm-.01-14C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z" />
                        </svg>
                        <span className="side-menu__label">Users</span>
                      </Link>
                    </li>
                  )}

                  {hasModuleAccess('trades', loggedAdmin) && (
                    <li className="slide">
                      <Link to="/trades" className={`side-menu__item ${isRouteActive('/trades') ? 'active' : ''}`}>
                        <svg xmlns="http://www.w3.org/2000/svg" className="side-menu__icon" viewBox="0 0 24 24">
                          <path d="M0 0h24v24H0V0z" fill="none" />
                          <path d="M19 5H5v14h14V5zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z" opacity=".3" />
                          <path d="M3 5v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2zm2 0h14v14H5V5zm2 5h2v7H7zm4-3h2v10h-2zm4 6h2v4h-2z" />
                        </svg>
                        <span className="side-menu__label">Stakes</span>
                      </Link>
                    </li>
                  )}

                  {hasModuleAccess('rank', loggedAdmin) && (
                    <li className="slide">
                      <Link to="/rank" className={`side-menu__item ${isRouteActive('/rank') ? 'active' : ''}`}>
                        <svg xmlns="http://www.w3.org/2000/svg" className="side-menu__icon" viewBox="0 0 24 24">
                          <path d="M0 0h24v24H0V0z" fill="none" />
                          <path d="M19 5H5v14h14V5zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z" opacity=".3" />
                          <path d="M3 5v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2zm2 0h14v14H5V5zm2 5h2v7H7zm4-3h2v10h-2zm4 6h2v4h-2z" />
                        </svg>
                        <span className="side-menu__label">Rank</span>
                      </Link>
                    </li>
                  )}

                  {hasModuleAccess('rewards', loggedAdmin) && (
                    <li className="slide">
                      <Link to="/rewards" className={`side-menu__item ${isRouteActive('/rewards') ? 'active' : ''}`}>
                        <svg xmlns="http://www.w3.org/2000/svg" className="side-menu__icon" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M0 0h24v24H0z" fill="none" />
                          <path d="M20 6h-2.18c.11-.31.18-.65.18-1a3 3 0 0 0-3-3c-1.39 0-2.55.95-2.88 2.24L12 4.74l-.12-.5C11.55 2.95 10.39 2 9 2a3 3 0 0 0-3 3c0 .35.07.69.18 1H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-5-2c.55 0 1 .45 1 1s-.45 1-1 1h-2.22l.53-1.63C13.52 4.15 14.21 4 15 4zM9 4c.79 0 1.48.15 1.69.37L11.22 6H9c-.55 0-1-.45-1-1s.45-1 1-1zm11 15H4v-2h16v2zm0-5H4V8h5.08L7 13h2l2-5h2l2 5h2l-2.08-5H20v6z" />
                        </svg>
                        <span className="side-menu__label">Rewards & Offers</span>
                      </Link>
                    </li>
                  )}
                </>
              )}

              {/* Wallets & Distribution Category */}
              {(hasModuleAccess('matching', loggedAdmin) || hasModuleAccess('withdrawals', loggedAdmin)) && (
                <>
                  <li className="slide__category">
                    <span className="category-name">Wallets</span>
                  </li>

                  {hasModuleAccess('matching', loggedAdmin) && (
                    <li className="slide">
                      <Link to="/matching-bonus" className={`side-menu__item ${isRouteActive('/matching-bonus') ? 'active' : ''}`}>
                        <svg xmlns="http://www.w3.org/2000/svg" className="side-menu__icon" viewBox="0 0 24 24">
                          <path d="M0 0h24v24H0V0z" fill="none" />
                          <path d="M19 5H5v14h14V5zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z" opacity=".3" />
                          <path d="M3 5v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2zm2 0h14v14H5V5zm2 5h2v7H7zm4-3h2v10h-2zm4 6h2v4h-2z" />
                        </svg>
                        <span className="side-menu__label">Matching Bonus</span>
                      </Link>
                    </li>
                  )}

                  {hasModuleAccess('withdrawals', loggedAdmin) && (
                    <li className="slide">
                      <Link to="/client-withdraw" className={`side-menu__item ${isRouteActive('/client-withdraw') ? 'active' : ''}`}>
                        <svg xmlns="http://www.w3.org/2000/svg" className="side-menu__icon" viewBox="0 0 24 24">
                          <path d="M0 0h24v24H0V0z" fill="none" />
                          <path d="M19 5H5v14h14V5zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z" opacity=".3" />
                          <path d="M3 5v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2zm2 0h14v14H5V5zm2 5h2v7H7zm4-3h2v10h-2zm4 6h2v4h-2z" />
                        </svg>
                        <span className="side-menu__label">Withdraw History</span>
                      </Link>
                    </li>
                  )}
                </>
              )}

              {/* Master Wallet Category */}
              {hasModuleAccess('master_wallet', loggedAdmin) && (
                <>
                  <li className="slide__category">
                    <span className="category-name">Master Wallet</span>
                  </li>

                  <li className="slide">
                    <Link to="/master-wallet" className={`side-menu__item ${isRouteActive('/master-wallet') ? 'active' : ''}`}>
                      <svg xmlns="http://www.w3.org/2000/svg" className="side-menu__icon" viewBox="0 0 24 24">
                        <path d="M0 0h24v24H0V0z" fill="none" />
                        <path d="M5 9h14V5H5v4zm2-3.5c.83 0 1.5.67 1.5 1.5S7.83 8.5 7 8.5 5.5 7.83 5.5 7 6.17 5.5 7 5.5zM5 19h14v-4H5v4zm2-3.5c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5-1.5-.67-1.5-1.5.67-1.5 1.5-1.5z" opacity=".3" />
                        <path d="M20 13H4c-.55 0-1 .45-1 1v6c0 .55.45 1 1 1h16c.55 0 1-.45 1-1v-6c0-.55-.45-1-1-1zm-1 6H5v-4h14v4zm-12-.5c.83 0 1.5-.67 1.5-1.5s-.67-1.5-1.5-1.5-1.5.67-1.5 1.5.67 1.5 1.5 1.5zM20 3H4c-.55 0-1 .45-1 1v6c0 .55.45 1 1 1h16c.55 0 1-.45 1-1V4c0-.55-.45-1-1-1zm-1 6H5V5h14v4zM7 8.5c.83 0 1.5-.67 1.5-1.5S7.83 5.5 7 5.5 5.5 6.17 5.5 7 6.17 8.5 7 8.5z" />
                      </svg>
                      <span className="side-menu__label">Master Wallet</span>
                    </Link>
                  </li>
                </>
              )}

              {/* Blockchain Category */}
              {hasModuleAccess('treasury', loggedAdmin) && (
                <>
                  <li className="slide__category">
                    <span className="category-name">Blockchain</span>
                  </li>

                  <li className={`slide has-sub ${isOpenBlockchain ? 'open' : ''}`}>
                    <a
                      href="#blockchain"
                      className={`side-menu__item ${(isRouteActive('/treasury') || isRouteActive('/blockchain')) ? 'active' : ''}`}
                      onClick={toggleBlockchainMenu}
                      style={{ cursor: 'pointer' }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="side-menu__icon" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M0 0h24v24H0z" fill="none" />
                        <path d="M12 2L2 7l10 5 10-5-10-5zm0 18l-8-4V9.5l8 4 8-4V16l-8 4z" opacity=".3" />
                        <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                      </svg>
                      <span className="side-menu__label">Blockchain</span>
                      <i className={`fe ${isOpenBlockchain ? 'fe-chevron-down' : 'fe-chevron-right'} side-menu__angle ms-auto`}></i>
                    </a>
                    <ul className="slide-menu child1" style={{ display: isOpenBlockchain ? 'block' : 'none', paddingLeft: '1.5rem' }}>
                      <li className="slide">
                        <Link
                          to="/treasury"
                          className={`side-menu__item ${isRouteActive('/treasury') ? 'active' : ''}`}
                        >
                          <i className="fa fa-university me-2 fs-12 text-warning"></i>
                          <span className="side-menu__label">Treasury</span>
                        </Link>
                      </li>
                    </ul>
                  </li>
                </>
              )}

              {/* Management Category */}
              {(hasModuleAccess('support', loggedAdmin) || hasModuleAccess('subadmins', loggedAdmin) || hasModuleAccess('notifications', loggedAdmin) || hasModuleAccess('settings', loggedAdmin)) && (
                <>
                  <li className="slide__category">
                    <span className="category-name">Management</span>
                  </li>

                  {hasModuleAccess('support', loggedAdmin) && (
                    <li className={`slide has-sub ${isOpenForms ? 'open' : ''}`}>
                      <Link to="/support" className={`side-menu__item ${isRouteActive('/support') ? 'active' : ''}`} onClick={toggleMenuForms}>
                        <svg xmlns="http://www.w3.org/2000/svg" className="side-menu__icon" viewBox="0 0 24 24">
                          <path d="M0 0h24v24H0V0z" fill="none" />
                          <path d="M4 4h16v12H5.17L4 17.17V4zm14 10V6H6v8h12z" opacity=".3" />
                          <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H5.17L4 17.17V4h16v12z" />
                        </svg>
                        <span className="side-menu__label">Support Tickets</span>
                      </Link>
                    </li>
                  )}

                  {hasModuleAccess('subadmins', loggedAdmin) && (
                    <li className="slide">
                      <Link to="/admin" className={`side-menu__item ${isRouteActive('/admin') ? 'active' : ''}`}>
                        <svg xmlns="http://www.w3.org/2000/svg" className="side-menu__icon" viewBox="0 0 24 24">
                          <path d="M0 0h24v24H0V0z" fill="none" />
                          <path d="M12 4c-4.42 0-8 3.58-8 8s3.58 8 8 8 8-3.58 8-8-3.58-8-8-8zm3.5 4c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5-1.5-.67-1.5-1.5.67-1.5 1.5-1.5zm-7 0c.83 0 1.5.67 1.5 1.5S9.33 11 8.5 11 7 10.33 7 9.5 7.67 8 8.5 8zm3.5 9.5c-2.33 0-4.32-1.45-5.12-3.5h1.67c.7 1.19 1.97 2 3.45 2s2.76-.81 3.45-2h1.67c-.8 2.05-2.79 3.5-5.12 3.5z" opacity=".3" />
                          <circle cx="15.5" cy="9.5" r="1.5" />
                          <circle cx="8.5" cy="9.5" r="1.5" />
                          <path d="M12 16c-1.48 0-2.75-.81-3.45-2H6.88c.8 2.05 2.79 3.5 5.12 3.5s4.32-1.45 5.12-3.5h-1.67c-.69 1.19-1.97 2-3.45 2zm-.01-14C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z" />
                        </svg>
                        <span className="side-menu__label">Admin Management</span>
                      </Link>
                    </li>
                  )}

                  {hasModuleAccess('notifications', loggedAdmin) && (
                    <li className="slide">
                      <Link to="/notification" className={`side-menu__item ${isRouteActive('/notification') ? 'active' : ''}`}>
                        <svg xmlns="http://www.w3.org/2000/svg" className="side-menu__icon" viewBox="0 0 24 24">
                          <path d="M0 0h24v24H0V0z" fill="none" />
                          <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2zm-2 1H8v-6c0-2.48 1.51-4.5 4-4.5s4 2.02 4 4.5v6z" />
                        </svg>
                        <span className="side-menu__label">Notifications</span>
                      </Link>
                    </li>
                  )}

                  {/* {hasModuleAccess('settings', loggedAdmin) && (
                    <li className="slide">
                      <Link to="/setting" className={`side-menu__item ${isRouteActive('/setting') ? 'active' : ''}`}>
                        <svg xmlns="http://www.w3.org/2000/svg" className="side-menu__icon" viewBox="0 0 24 24">
                          <path d="M0 0h24v24H0V0z" fill="none" />
                          <path d="M19.14 12.94c.04-.3.06-.6.06-.94s-.02-.64-.06-.94l2.03-1.58c.18-.14.22-.41.1-.61l-1.92-3.32c-.12-.21-.38-.28-.6-.18l-2.4 1a7.987 7.987 0 0 0-1.74-.99l-.36-2.56A.5.5 0 0 0 14 2h-4a.5.5 0 0 0-.49.42l-.36 2.56c-.61.2-1.2.48-1.74.99l-2.4-1c-.22-.09-.48-.02-.6.18L2.73 8.87a.5.5 0 0 0 .1.61l2.03 1.58c-.04.3-.06.6-.06.94s.02.64.06.94l-2.03 1.58a.5.5 0 0 0-.1.61l1.92 3.32c.12.21.38.28.6.18l2.4-1c.54.51 1.13.79 1.74.99l.36 2.56c.03.27.24.46.49.46h4c.25 0 .46-.19.49-.46l.36-2.56c.61-.2 1.2-.48 1.74-.99l2.4 1c.22.09.48.02.6-.18l1.92-3.32a.5.5 0 0 0-.1-.61l-2.03-1.58zM12 15c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3z" />
                        </svg>
                        <span className="side-menu__label">Settings</span>
                      </Link>
                    </li>
                  )} */}
                </>
              )}

              {/* Change Password */}
              <li className="slide">
                <button
                  type="button"
                  onClick={() => {
                    setPasswordError('');
                    setCurrentPassword('');
                    setNewPassword('');
                    setConfirmPassword('');
                    setShowPasswordModal(true);
                  }}
                  className="side-menu__item btn btn-link text-start w-100 border-0"
                  style={{ textDecoration: 'none' }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="side-menu__icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M0 0h24v24H0V0z" fill="none" />
                    <path d="M12.65 10C11.83 7.67 9.61 6 7 6c-3.31 0-6 2.69-6 6s2.69 6 6 6c2.61 0 4.83-1.67 5.65-4H17v4h4v-4h2v-4H12.65zM7 14c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z"/>
                  </svg>
                  <span className="side-menu__label">Change Password</span>
                </button>
              </li>

              {/* Logout */}
              <li className="slide">
                <button
                  type="button"
                  onClick={logout}
                  className="side-menu__item btn btn-link text-start w-100 border-0 text-danger"
                  style={{ textDecoration: 'none' }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="side-menu__icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M0 0h24v24H0V0z" fill="none" />
                    <path d="M17 7l-1.41 1.41L18.17 11H7v2h11.17l-2.58 2.59L17 17l5-5-5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" />
                  </svg>
                  <span className="side-menu__label">Logout</span>
                </button>
              </li>
            </ul>

            <div className="slide-right" id="slide-right">
              <svg xmlns="http://www.w3.org/2000/svg" fill="#7b8191" width={24} height={24} viewBox="0 0 24 24">
                <path d="M10.707 17.707 16.414 12l-5.707-5.707-1.414 1.414L13.586 12l-4.293 4.293z" />
              </svg>
            </div>
          </nav>
        </div>
      </aside>

      {/* Page Content with Permission Access Gate */}
      {isPageAllowed ? (
        children
      ) : (
        <div className="main-content app-content">
          <div className="container-fluid py-5">
            <div className="card shadow-sm border-0 text-center py-5 my-5 rounded-4">
              <div className="card-body py-5">
                <div className="mb-4">
                  <div
                    className="rounded-circle bg-danger-transparent d-inline-flex align-items-center justify-content-center text-danger"
                    style={{ width: '90px', height: '90px' }}
                  >
                    <i className="fa fa-lock" style={{ fontSize: '42px' }}></i>
                  </div>
                </div>
                <h3 className="fw-bold text-dark mb-2">Access Restricted</h3>
                <p className="text-muted fs-14 mb-4" style={{ maxWidth: '480px', margin: '0 auto' }}>
                  Your account does not have permission to access the <strong>{PERMISSION_MODULES.find(m => m.key === currentModuleKey)?.label || 'requested'}</strong> module. Please contact your <strong>Master Administrator</strong> to request access.
                </p>
                <div className="d-flex justify-content-center gap-2">
                  <Link to="/" className="btn btn-primary btn-sm px-4">
                    <i className="fa fa-home me-1"></i> Go to Dashboard
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Change Password Modal for Admin & Sub-Admin */}
      {showPasswordModal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg rounded-3">
              <div className="modal-header bg-primary text-white py-3">
                <h5 className="modal-title fs-16 fw-bold">
                  <i className="fa fa-key me-2"></i>Change Account Password
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowPasswordModal(false)}
                  disabled={isChangingPassword}
                ></button>
              </div>

              <form onSubmit={handleChangePassword}>
                <div className="modal-body p-4">
                  {passwordError && (
                    <div className="alert alert-danger py-2 fs-13 mb-3">
                      <i className="fa fa-exclamation-circle me-1"></i> {passwordError}
                    </div>
                  )}

                  <div className="mb-3">
                    <label className="form-label fs-12 fw-semibold text-dark">Current Password *</label>
                    <div className="input-group">
                      <span className="input-group-text bg-light text-muted">
                        <i className="fa fa-lock"></i>
                      </span>
                      <input
                        type={showCurrentPass ? 'text' : 'password'}
                        className="form-control"
                        placeholder="Enter current password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        required
                        autoFocus
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => setShowCurrentPass(!showCurrentPass)}
                      >
                        <i className={`fa ${showCurrentPass ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                      </button>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fs-12 fw-semibold text-dark">New Password *</label>
                    <div className="input-group">
                      <span className="input-group-text bg-light text-muted">
                        <i className="fa fa-shield-alt"></i>
                      </span>
                      <input
                        type={showNewPass ? 'text' : 'password'}
                        className="form-control"
                        placeholder="Minimum 6 characters"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        minLength="6"
                        required
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => setShowNewPass(!showNewPass)}
                      >
                        <i className={`fa ${showNewPass ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                      </button>
                    </div>
                    <small className="text-muted fs-11">Must be at least 6 characters long.</small>
                  </div>

                  <div className="mb-2">
                    <label className="form-label fs-12 fw-semibold text-dark">Confirm New Password *</label>
                    <div className="input-group">
                      <span className="input-group-text bg-light text-muted">
                        <i className="fa fa-check-double"></i>
                      </span>
                      <input
                        type={showConfirmPass ? 'text' : 'password'}
                        className="form-control"
                        placeholder="Re-enter new password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        minLength="6"
                        required
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => setShowConfirmPass(!showConfirmPass)}
                      >
                        <i className={`fa ${showConfirmPass ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                      </button>
                    </div>
                  </div>
                </div>

                <div className="modal-footer bg-light py-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    onClick={() => setShowPasswordModal(false)}
                    disabled={isChangingPassword}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-sm btn-primary d-flex align-items-center gap-1"
                    disabled={isChangingPassword}
                  >
                    {isChangingPassword ? (
                      <>
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="fa fa-save"></i> Update Password
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Header;